'use es6';

import I18n from 'I18n';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import { List } from 'immutable';
import { CALL, DEALS, EMAIL, LEADS, MEETING_EVENT, SEQUENCE_ENROLLMENTS, TASK, UNIFIED_ADS_CAMPAIGNS, UNIFIED_ADS_NETWORKS, ANALYTICS_FORMS, ANALYTICS_SOURCES, ANALYTICS_ALL_PAGES, ANALYTICS_UTM_CAMPAIGNS, ANALYTICS_UTM_CONTENTS, ANALYTICS_UTM_MEDIUMS, ANALYTICS_UTM_SOURCES, ANALYTICS_UTM_TERMS, ANALYTICS_DEVICE_TYPE_DETAILS, ANALYTICS_GEOLOCATION, ANALYTICS_BROWSER_DETAILS, ANALYTICS_TOPICS } from 'reporting-data/constants/dataTypes';
import getMetricLabel from 'reporting-data/public/get-metric-label';
import { toMetricKey } from 'reporting-data/v2/dataset/datasetMetrics';
import { getDataTypeName, isUnifiedAnalyticsReport } from 'sales-analytics-lib/utils/salesAnalyticsHelpers';
import { ADS_METRICS, BROWSERS_METRICS, CALL_METRICS, DEVICE_TYPES_METRICS, GEOLOCATION_METRICS, LEAD_CAPTURE_METRICS, LEAD_METRICS, MARKETING_EMAIL_METRICS, MEETING_METRICS, PAGES_METRICS, SALES_VELOCITY_METRICS, SEQUENCE_ENROLLMENT_METRICS, SOURCES_METRICS, TASK_METRICS, UTM_PARAM_METRICS, TOPIC_CLUSTERS_METRICS } from './inDevReportMetricEditorSchemas';
import { SUB_APPS } from 'reporting-data/tsTypes/subAppTypes';
const METRIC_KEY_DELIMITER = '|';
const UNIFIED_ANALYTICS_DATA_TYPE_ALIAS = 'UNIFIED_ANALYTICS';
const subAppDataTypeTableOnlyMetrics = {
  [SUB_APPS.pages]: {
    [ANALYTICS_ALL_PAGES]: ['COUNT|CREATED_TIMESTAMP', 'COUNT|PUBLISHED_TIMESTAMP', 'COUNT|PUBLISH_STATUS_ID', 'COUNT|DOMAIN_ID', 'COUNT|CONTENT_GROUP_ID', 'COUNT|BLOG_AUTHOR_ID', 'COUNT|LANGUAGE', 'COUNT|URL', 'COUNT|HTML_TITLE', 'COUNT|CONTENT_TYPE_CODE']
  },
  [SUB_APPS.leadCapture]: {
    [ANALYTICS_FORMS]: ['COUNT|TYPE_ID']
  }
};

// While these are not required to power the mult metric control when viewing reports in the analytics UI,
// these are required to properly add back the metric editor when viewing saved analytics reports.
export const subAppDataTypeMetrics = {
  [SUB_APPS.prospectEngagement]: {
    [CALL]: CALL_METRICS,
    [MEETING_EVENT]: MEETING_METRICS,
    [SEQUENCE_ENROLLMENTS]: SEQUENCE_ENROLLMENT_METRICS,
    [TASK]: TASK_METRICS
  },
  [SUB_APPS.leadFunnel]: {
    [LEADS]: LEAD_METRICS
  },
  [SUB_APPS.salesVelocity]: {
    [DEALS]: SALES_VELOCITY_METRICS
  },
  [SUB_APPS.ads]: {
    [UNIFIED_ADS_CAMPAIGNS]: ADS_METRICS,
    [UNIFIED_ADS_NETWORKS]: ADS_METRICS
  },
  [SUB_APPS.leadCapture]: {
    [ANALYTICS_FORMS]: LEAD_CAPTURE_METRICS
  },
  [SUB_APPS.marketingEmails]: {
    [EMAIL]: MARKETING_EMAIL_METRICS
  },
  [SUB_APPS.sources]: {
    [ANALYTICS_SOURCES]: SOURCES_METRICS
  },
  [SUB_APPS.pages]: {
    [ANALYTICS_ALL_PAGES]: PAGES_METRICS
  },
  [SUB_APPS.utmParameters]: {
    [ANALYTICS_UTM_CAMPAIGNS]: UTM_PARAM_METRICS,
    [ANALYTICS_UTM_CONTENTS]: UTM_PARAM_METRICS,
    [ANALYTICS_UTM_MEDIUMS]: UTM_PARAM_METRICS,
    [ANALYTICS_UTM_SOURCES]: UTM_PARAM_METRICS,
    [ANALYTICS_UTM_TERMS]: UTM_PARAM_METRICS
  },
  [SUB_APPS.deviceTypes]: {
    [ANALYTICS_DEVICE_TYPE_DETAILS]: DEVICE_TYPES_METRICS
  },
  [SUB_APPS.geolocation]: {
    [ANALYTICS_GEOLOCATION]: GEOLOCATION_METRICS
  },
  [SUB_APPS.browsers]: {
    [ANALYTICS_BROWSER_DETAILS]: BROWSERS_METRICS
  },
  [SUB_APPS.topicClusters]: {
    [ANALYTICS_TOPICS]: TOPIC_CLUSTERS_METRICS
  }
};
export const getMetricsForSubAppDataType = (subAppKey, dataType, options = {}) => {
  const {
    getTableOnlyMetrics
  } = options;
  const metricMap = getTableOnlyMetrics ? subAppDataTypeTableOnlyMetrics : subAppDataTypeMetrics;
  const subAppMetrics = metricMap[subAppKey] || {};
  return subAppMetrics[dataType] || [];
};
export const getMetricKey = (property, metricType) => `${metricType}${METRIC_KEY_DELIMITER}${property}`;
export const getMetricFieldsFromKey = metricKey => {
  const splitKey = metricKey.split(METRIC_KEY_DELIMITER);
  return {
    metricTypes: [splitKey[0]],
    property: splitKey[1]
  };
};
export const getMetricOptionKeys = (report, subAppKey, options = {}) => {
  const dataType = getDataTypeName(report);
  return getMetricsForSubAppDataType(subAppKey, dataType, options);
};
export const getMetricOptions = (report, subAppKey, options = {}) => {
  const dataType = getDataTypeName(report);
  // Since the legacy metric control relies on FE translations for metric labels,
  // we created a dedicated translation key for UA data types since most options
  // overlap and we wanted to avoid creating duplicate translation keys for each UA data type.
  const dataTypeAliasForTranslations = isUnifiedAnalyticsReport(report) ? UNIFIED_ANALYTICS_DATA_TYPE_ALIAS : dataType;
  const metricKeys = getMetricsForSubAppDataType(subAppKey, dataType, options);
  const metricOptions = metricKeys.map(metricKey => {
    const {
      property,
      metricTypes: [metricType]
    } = getMetricFieldsFromKey(metricKey);
    const propertyLabel = I18n.text(`sales-analytics-lib.metricSelect.options.${dataTypeAliasForTranslations}.${property}`);
    return {
      text: getMetricLabel(propertyLabel, metricType, property),
      value: metricKey
    };
  });
  return metricOptions;
};
export const getTableOnlyMetricOptionKeys = (report, subAppKey) => getMetricOptionKeys(report, subAppKey, {
  getTableOnlyMetrics: true
});
export const getTableOnlyMetricOptions = (report, subAppKey) => getMetricOptions(report, subAppKey, {
  getTableOnlyMetrics: true
});
export const getMetricTooltip = (dataType, metric) => {
  const {
    property,
    metricTypes: [metricType]
  } = metric;
  const prefix = `reporting-data.properties.crm-object.tooltips.${dataType}`;
  const withoutMetricType = `${prefix}.${property}`;
  const withMetricType = `${withoutMetricType}_${metricType}`;
  if (isValidI18nKey(withMetricType)) {
    return withMetricType;
  } else if (isValidI18nKey(withoutMetricType)) {
    return withoutMetricType;
  }
  return null;
};
export const getReportTemplateTableMetrics = report => {
  const metrics = report.getIn(['config', 'metrics']) || List();
  return metrics.map(metricObject => {
    const property = metricObject.get('property');
    const metricType = metricObject.get('metricTypes').first();
    return toMetricKey({
      property,
      type: metricType
    });
  });
};